import { AnimatePresence, motion } from "framer-motion";
import type { FC, RefObject, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import IconChild from "../../../assets/icons/IconChild";
import IconUserRound from "../../../assets/icons/IconUserRound";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { getQueryString } from "../../../assets/Utils/search";
import { useSearch } from "../../hooks/useSearch";
import { ScrollLock } from "./Search";

type GuestsModalProps = {
  isGuestsOpen: boolean;
  guestsRef: RefObject<HTMLDivElement>;
  mode: "mobile" | "desktop";
  shouldOpenOver?: boolean;
  isNav?: boolean;
  setIsGuestsOpen: Dispatch<SetStateAction<boolean>>;
  setIsLocationOpen: Dispatch<SetStateAction<boolean>>;
  parent: "home" | "nav" | "detail";
};

export const GuestsModal: FC<GuestsModalProps> = ({
  isGuestsOpen,
  guestsRef,
  mode,
  shouldOpenOver,
  isNav = false,
  setIsGuestsOpen,
  setIsLocationOpen,
  parent
}) => {
  const { t } = useTranslation();

  const { searchParameters, setParameters } = useSearch();

  const { pathname } = useLocation();

  const addAdult = () =>
    setParameters((p) => ({
      ...p,
      adults: p.adults + 1,
    }));

  const removeAdult = () =>
    setParameters((p) => ({
      ...p,
      adults: p.adults > 1 ? p.adults - 1 : 1,
    }));

  const addChild = () =>
    setParameters((p) => ({
      ...p,
      childrenAge: [...searchParameters.childrenAge, 10],
    }));

  const removeChild = () =>
    setParameters((p) => ({
      ...p,
      childrenAge: searchParameters.childrenAge.slice(0, -1),
    }));

  const guests = searchParameters.adults + searchParameters.childrenAge.length;

  return (
    <AnimatePresence>
      {isGuestsOpen && (
        <>
          <ScrollLock />
          <motion.div
            ref={guestsRef} // Add ref here
            className={mode === "desktop" ? "rounded-3xl px-5 py-3" : ""}
            style={
              mode === "desktop"
                ? {
                    position: "absolute",
                    ...(shouldOpenOver
                      ? { bottom: !isNav ? "115%" : "140%" }
                      : { top: !isNav ? "115%" : "140%" }),
                    zIndex: 100,
                    overflow: "hidden",
                    border: "1px solid lightgray",
                    boxShadow: "4px 4px 10px 0px rgba(0, 0, 0, 0.05)",
                    background: "white",
                  }
                : {
                    position: "fixed",
                    top: "0px",
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    overflow: "hidden",
                    border: "1px solid lightgray",
                    boxShadow: "4px 4px 10px 0px rgba(0, 0, 0, 0.05)",
                    background: "white",
                    height: "100dvh",
                  }
            }
            initial={
              mode === "desktop" ? { opacity: 0 } : { y: 100, opacity: 0 }
            }
            animate={mode === "desktop" ? { opacity: 1 } : { y: 0, opacity: 1 }}
            exit={
              mode === "desktop"
                ? { y: -20, opacity: 0 }
                : { y: 100, opacity: 0 }
            }
            transition={{ duration: 0.2 }}
          >
            {mode === "mobile" && (
              <div className="relative flex justify-end px-3 items-center box-border border-bottom h-12">
                <h3 className="font-medium text-sm text-center mb-0 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                  {t("ds.choose_guests")}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsGuestsOpen(false)}
                  className="bg-transparent border-none touch-hitbox relative"
                >
                  <IconNew icon="close" className="size-5" />
                </button>
              </div>
            )}
            <div
              style={{ width: mode === "desktop" ? "300px" : "unset" }}
              className="flex flex-col gap-3 w-full px-3 lg:px-0 h-[calc(100%-9rem)] overflow-y-auto border-bottom lg:border-0 py-6 lg:py-3"
            >
              {mode === "mobile" && (
                <h3 className="text-gray-600 font-medium text-sm">
                  {t("ds.select_guests")}
                </h3>
              )}
              <div className="flex flex-col gap-6">
                <div className="w-full flex items-center gap-4">
                  <div className="size-10 rounded-xl bg-gray-50 flex items-center justify-center">
                    <IconUserRound className="size-4" />
                  </div>
                  <div className="flex flex-col flex-1">
                    <h4 className="font-medium text-sm mb-0">
                      {t("ds.adults")}
                    </h4>
                    <p className="font-medium text-xs text-gray-400 min-w-28">
                      {t("ds.guests_adults_subtitle")}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="size-7 flex justify-center items-center rounded-full border border-gray-500 text-lg text-gray-500 bg-transparent disabled:opacity-50"
                      disabled={searchParameters.adults === 1}
                      onClick={removeAdult}
                    >
                      -
                    </button>
                    <span className="font-normal text-base w-10 text-center">
                      {searchParameters.adults}
                    </span>
                    <button
                      type="button"
                      className="size-7 flex justify-center items-center rounded-full border border-gray-500 text-lg text-gray-500 bg-transparent"
                      onClick={addAdult}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="w-full flex items-center gap-4">
                  <div className="size-10 rounded-xl bg-gray-50 flex items-center justify-center">
                    <IconChild className="size-[1.125rem]" />
                  </div>
                  <div className="flex flex-col flex-1">
                    <h4 className="font-medium text-sm mb-0">
                      {t("ds.guests_kids")}
                    </h4>
                    <p className="font-medium text-xs text-gray-400 min-w-28">
                      {t("ds.guests_kids_subtitle")}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="size-7 flex justify-center items-center rounded-full border border-gray-500 text-lg text-gray-500 bg-transparent disabled:opacity-50"
                      disabled={searchParameters.childrenAge.length === 0}
                      onClick={removeChild}
                    >
                      -
                    </button>
                    <span className="font-normal text-base w-10 text-center">
                      {searchParameters.childrenAge.length}
                    </span>
                    <button
                      type="button"
                      className="size-7 flex justify-center items-center rounded-full border border-gray-500 text-lg text-gray-500 bg-transparent"
                      onClick={addChild}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* <div className="w100 d-flex justify-content-between align-items-center">
									<span className="fs-body medium">{t("ds.guests_adults")}</span>

									<div className="d-flex align-items-center">
										<CircularButton onClick={removeAdult}>-</CircularButton>
										<span className="width-40 text-center fs-body medium">
											{params.adults}
										</span>
										<CircularButton onClick={addAdult}>+</CircularButton>
									</div>
								</div>
								<div className="w100 d-flex justify-content-between align-items-center">
									<span className="fs-body medium">{t("ds.guests_kids")}</span>

									<div className="d-flex align-items-center">
										<CircularButton onClick={removeChild}>-</CircularButton>
										<span className="width-40 text-center fs-body-lg medium">
											{params.childrenAge.length}
										</span>
										<CircularButton onClick={addChild}>+</CircularButton>
									</div>
								</div> */}
            </div>

            {mode === "mobile" && <div className="height-25"></div>}

            {mode === "mobile" && (
              <div className="w-full h-24 px-3 fixed bottom-0 left-0 flex items-center justify-center">
                <Button
                  onClick={() => {
                    if (parent === "detail") {
                      const queryString = getQueryString(searchParameters);
                      window.location.href = `${String(
                        process.env.REACT_APP_FRONTEND_URI,
                      )}${pathname}?${queryString}`;
                      return;
                    }

                    setIsGuestsOpen(false);

                    if (
                      !searchParameters.destinationLabel &&
                      !searchParameters.tag
                    ) {
                      setIsLocationOpen(true);
                      return;
                    }

                    if (
                      !searchParameters.arrivalDate ||
                      !searchParameters.departureDate
                    ) {
                      setIsGuestsOpen(true);
                    }


                  }}
                  className="w-full"
                  text={`${t("ds.next")} ‧ ${guests} ${t("ds.guest", {
                    count: guests,
                  }).toLowerCase()}`}
                />
              </div>
            )}

            {parent === "detail" && mode === "desktop" && (
                <div
                  className="w-full flex justify-end pt-4"
                >
                  <Button
                    size="sm"
                    className="h-12"
                    textClass="text-sm font-medium"
                    text={t("ds.search")}
                    onClick={() => {
                      const queryString = getQueryString(searchParameters);
                      window.location.href = `${String(
                        process.env.REACT_APP_FRONTEND_URI,
                      )}${pathname}?${queryString}`;
                    }}
                  />
                </div>
              )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GuestsModal;
